import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './styles/responsive.css';
import ErrorBoundary from './components/ErrorBoundary';
import MainLayout from './components/layout/MainLayout';
import { ToastContainer } from './components/Toast';
import useToast from './hooks/useToast';
import { LanguageProvider } from './contexts/LanguageContext';
import Home from './pages/Home';
import AboutUs from './pages/public/AboutUs';
import Research from './pages/public/Research';
import Internships from './pages/public/Internships';
import Courses from './pages/public/Courses';
import Faculty from './pages/public/Faculty';
import VerifyCertificate from './pages/public/VerifyCertificate';
import ContactUs from './pages/public/ContactUs';
import Pricing from './pages/public/Pricing';
import SuccessStories from './pages/public/SuccessStories';
import Methodology from './pages/public/Methodology';
import LiveClasses from './pages/public/LiveClasses';
import LiveClassRoom from './pages/LiveClassRoom';
import { Blog, FAQ, Docs } from './pages/public/Resources';
import CourseDetail from './pages/public/CourseDetail_NEW';
import CourseLearn from './pages/public/CourseLearn';
import LessonActivity from './pages/public/LessonActivity';
import Practice from './pages/Practice';
import Rewards from './pages/Rewards';
import Privacy from './pages/public/legal/Privacy';
import Terms from './pages/public/legal/Terms';
import Refund from './pages/public/legal/Refund';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import Shop from './pages/student/Shop';
import StudentDashboard from './pages/student/StudentDashboard';
import StudentAnalyticsDashboard from './components/StudentAnalyticsDashboard';
import CourseDiscovery from './pages/CourseDiscovery';
import InstructorDashboard from './pages/InstructorDashboard';
import LiveClassManager from './pages/instructor/LiveClassManager';
import AdminDashboard from './pages/AdminDashboard';
import ProtectedRoute from './components/ProtectedRoute';
import ConsentModal from './components/ConsentModal';
// New pages
import Notifications from './pages/student/Notifications';
import PaymentCheckout from './pages/student/PaymentCheckout';
import Achievements from './pages/student/Achievements';
import Settings from './pages/student/Settings';
import Recommendations from './pages/student/Recommendations';
import PerformanceAnalytics from './pages/student/PerformanceAnalytics';
import Subscriptions from './pages/student/Subscriptions';
import Certificates from './pages/student/Certificates';
import QuizResults from './pages/student/QuizResults';
import ResetPassword from './pages/ResetPassword';
import Leaderboard from './components/Leaderboard';
import NotificationPreferences from './pages/NotificationPreferences';
import AnalyticsTracker from './components/AnalyticsTracker';
import SeoManager from './components/SeoManager';
import ScrollToTop from './components/ScrollToTop';
import './styles/App.css';

function App() {
  const { toasts, removeToast } = useToast();

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <Router>
          <AnalyticsTracker />
          <SeoManager />
          <ScrollToTop />
          <ConsentModal />
          <ToastContainer toasts={toasts} removeToast={removeToast} />
          <Routes>

        {/* Public Routes with Main Layout (Navbar + Footer) */}
        <Route path="/" element={<MainLayout><Home /></MainLayout>} />
        <Route path="/about" element={<MainLayout><AboutUs /></MainLayout>} />
        <Route path="/research" element={<MainLayout><Research /></MainLayout>} />
        <Route path="/internships" element={<MainLayout><Internships /></MainLayout>} />
        <Route path="/courses" element={<MainLayout><Courses /></MainLayout>} />
        <Route path="/live-classes" element={<MainLayout><LiveClasses /></MainLayout>} />
        <Route path="/live-classes/:id" element={<ProtectedRoute><LiveClassRoom /></ProtectedRoute>} />
        <Route path="/courses/my" element={<MainLayout><Courses /></MainLayout>} />
        <Route path="/courses/free" element={<MainLayout><Courses /></MainLayout>} />
        <Route path="/courses/paid" element={<MainLayout><Courses /></MainLayout>} />
        <Route path="/courses/:id" element={<MainLayout><CourseDetail /></MainLayout>} />
        <Route path="/courses/:id/learn" element={<MainLayout><CourseLearn /></MainLayout>} />
        <Route path="/courses/:courseId/lesson/:lessonId/activity" element={<MainLayout><LessonActivity /></MainLayout>} />
        <Route path="/faculty" element={<MainLayout><Faculty /></MainLayout>} />
        <Route path="/verify" element={<MainLayout><VerifyCertificate /></MainLayout>} />
        <Route path="/contact" element={<MainLayout><ContactUs /></MainLayout>} />
        <Route path="/pricing" element={<MainLayout><Pricing /></MainLayout>} />
        <Route path="/success-stories" element={<MainLayout><SuccessStories /></MainLayout>} />
        <Route path="/methodology" element={<MainLayout><Methodology /></MainLayout>} />
        <Route path="/blog" element={<MainLayout><Blog /></MainLayout>} />
        <Route path="/faq" element={<MainLayout><FAQ /></MainLayout>} />
        <Route path="/docs" element={<MainLayout><Docs /></MainLayout>} />

        {/* Legal Routes */}
        <Route path="/privacy" element={<MainLayout><Privacy /></MainLayout>} />
        <Route path="/terms" element={<MainLayout><Terms /></MainLayout>} />
        <Route path="/refund-policy" element={<MainLayout><Refund /></MainLayout>} />

        <Route path="/practice" element={<MainLayout><Practice /></MainLayout>} />
        <Route path="/rewards" element={<MainLayout><Rewards /></MainLayout>} />

        {/* Auth Routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Dashboard Routes (Protected) */}
        <Route path="/student-dashboard" element={<ProtectedRoute allowedRoles="student"><MainLayout><StudentDashboard /></MainLayout></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute allowedRoles="student"><MainLayout><StudentAnalyticsDashboard /></MainLayout></ProtectedRoute>} />
        <Route path="/discover" element={<MainLayout><CourseDiscovery /></MainLayout>} />
        <Route path="/instructor-dashboard" element={<ProtectedRoute allowedRoles="instructor"><InstructorDashboard /></ProtectedRoute>} />
        <Route path="/instructor/live-classes" element={<ProtectedRoute allowedRoles="instructor"><MainLayout><LiveClassManager /></MainLayout></ProtectedRoute>} />
        <Route path="/admin-dashboard" element={<ProtectedRoute allowedRoles="admin"><AdminDashboard /></ProtectedRoute>} />
        <Route path="/shop" element={<ProtectedRoute><MainLayout><Shop /></MainLayout></ProtectedRoute>} />

        {/* Student Portal Routes (Protected) */}
        <Route path="/notifications" element={<ProtectedRoute><MainLayout><Notifications /></MainLayout></ProtectedRoute>} />
        <Route path="/notification-preferences" element={<ProtectedRoute><MainLayout><NotificationPreferences /></MainLayout></ProtectedRoute>} />
        <Route path="/checkout" element={<ProtectedRoute><MainLayout><PaymentCheckout /></MainLayout></ProtectedRoute>} />
        <Route path="/achievements" element={<ProtectedRoute><MainLayout><Achievements /></MainLayout></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><MainLayout><Settings /></MainLayout></ProtectedRoute>} />
        <Route path="/my-courses" element={<Navigate to="/courses/my" replace />} />
        <Route path="/recommendations" element={<ProtectedRoute><MainLayout><Recommendations /></MainLayout></ProtectedRoute>} />
        <Route path="/performance" element={<ProtectedRoute><MainLayout><PerformanceAnalytics /></MainLayout></ProtectedRoute>} />
        <Route path="/subscriptions" element={<ProtectedRoute><MainLayout><Subscriptions /></MainLayout></ProtectedRoute>} />
        <Route path="/certificates" element={<ProtectedRoute><MainLayout><Certificates /></MainLayout></ProtectedRoute>} />
        <Route path="/quiz/:quizId/results" element={<ProtectedRoute><MainLayout><QuizResults /></MainLayout></ProtectedRoute>} />
        <Route path="/leaderboard" element={<MainLayout><Leaderboard /></MainLayout>} />

        </Routes>
      </Router>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
